import {
  ChatCenteredIcon,
  DotsThreeIcon,
  FileTextIcon,
  GlobeIcon,
  PaperPlaneTiltIcon,
  RepeatIcon,
  ThumbsUpIcon,
} from '@phosphor-icons/react';
import { isLinkedInDocumentUrl, linkedInDocumentTitleFromUrl } from '@shared/linkedin-media-rules';
import React from 'react';

import { LinkedInImageGrid } from './LinkedInImageGrid';
import { MediaSlot } from './MediaSlot';
import { PreviewAvatar } from './PreviewAvatar';
import { PLATFORM_SKINS } from './platform-tokens';
import {
  DEFAULT_PREVIEW_BRAND_COLOR,
  type PlatformPreviewBaseProps,
  compactNumber,
  previewTimestampLabel,
  resolveAspectRatio,
  resolveCarouselAspectRatio,
} from './types';
import { useCaptionFold } from './useCaptionFold';
import { useCarousel } from './useCarousel';
import { useResolvedDarkMode } from './useResolvedDarkMode';

import { cn } from '@/lib/utils';

const FOLD_LENGTH = 210;

/**
 * LinkedIn feed post — header (avatar + name + headline + time), body with
 * "see more" fold at ~210 chars (LinkedIn's actual desktop fold), reaction
 * count row, action bar (Like / Comment / Repost / Send).
 *
 * Paints in LinkedIn's own colours via `PLATFORM_SKINS.linkedin`, not Mora's
 * semantic tokens — the preview system's §2 ruling, the same move
 * `FacebookPost.tsx` made first (read its header for the full reasoning and
 * for what is deliberately NOT converted).
 *
 * ⚠️ Provenance, unchanged and still true: LinkedIn has no frame in the
 * founder's Figma file, so this skin came from LinkedIn's published specs and
 * several of its values are flagged in `src/index.css` as best-effort
 * estimates rather than confirmed values. Converting to the skin does not
 * upgrade those estimates into facts.
 *
 * The outer border goes, matching `LinkedInAdPreview` — the paid sibling for
 * this platform. Every container this renders in already supplies the edge,
 * and the container owns elevation (see the doubled-shadow note in
 * `src/index.css`). Internal dividers keep `cardBorder`, which in LinkedIn's
 * dark skin is `#38434f` — a real divider colour, unlike Facebook's.
 */
export const LinkedInPost: React.FC<
  PlatformPreviewBaseProps & {
    headline?: string;
    /** Show the whole caption with no "see more" fold, for a reader who must judge every line. */
    captionExpanded?: boolean;
    /**
     * The article card LinkedIn draws under a post that shares a link: the article's image at 1.91:1, then its
     * headline and the site's domain. Shown only when the post has no media of its own, as on LinkedIn.
     */
    article?: { title?: string; domain: string; imageUrl?: string | null } | null;
  }
> = ({
  brandName,
  brandColor = DEFAULT_PREVIEW_BRAND_COLOR,
  avatarUrl,
  headline,
  captionExpanded = false,
  article = null,
  caption,
  imageUrl,
  imageUrls,
  imageAspectRatios,
  mediaItems,
  carouselActiveIndex,
  onCarouselActiveIndexChange,
  videoUrl,
  videoPoster,
  aspectRatio,
  focalX,
  focalY,
  zoom,
  timestamp,
  engagement,
  isLoading = false,
  mediaOverlay,
  className,
}) => {
  const { currentImage, currentVideoUrl, currentVideoPoster, carouselOverlay, safeActive } =
    useCarousel(imageUrls, imageUrl, {
      activeIndex: carouselActiveIndex,
      onActiveIndexChange: onCarouselActiveIndexChange,
      mediaItems,
    });
  // Several images draw as LinkedIn's collage, not a swipe carousel (see LinkedInImageGrid). A video among them
  // keeps the single-slot path, which LinkedIn does not allow in a post anyway.
  const gridUrls =
    !isLoading && imageUrls && imageUrls.length > 1 && !mediaItems?.some((i) => i.kind === 'video')
      ? imageUrls
      : null;
  const documentUrl = currentImage && isLinkedInDocumentUrl(currentImage) ? currentImage : null;
  const hasMedia = !!currentImage || !!currentVideoUrl || !!videoUrl || isLoading;
  const resolvedAspect = resolveAspectRatio(
    resolveCarouselAspectRatio(aspectRatio, imageAspectRatios, safeActive),
    1.91,
  );
  const { visible, needsFold, expanded, setExpanded } = useCaptionFold(
    caption,
    FOLD_LENGTH,
    captionExpanded,
  );
  const time = previewTimestampLabel(timestamp);
  const likes = engagement?.likes;
  const comments = engagement?.comments;

  const body = caption ?? '';
  // Reads the theme off `<html>` rather than `useTheme()`, which throws
  // outside a ThemeProvider — this renders in the calendar hover card, the
  // post inspector, the concept workbench and the style guide.
  const isDarkMode = useResolvedDarkMode();
  const skin = PLATFORM_SKINS.linkedin[isDarkMode ? 'dark' : 'light'];

  return (
    <article
      className={cn('w-full overflow-hidden rounded-lg', className)}
      data-platform="linkedin-post"
      style={{ backgroundColor: skin.cardBackground }}
    >
      <header className="flex items-start justify-between gap-3 px-3 pt-3 pb-2">
        <div className="flex items-start gap-2.5 min-w-0">
          <PreviewAvatar
            brandName={brandName}
            brandColor={brandColor}
            avatarUrl={avatarUrl}
            className="size-12 rounded-full"
            textClassName="text-sm font-semibold"
          />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold" style={{ color: skin.primaryText }}>
              {brandName}
            </p>
            {headline ? (
              <p className="line-clamp-1 text-detail" style={{ color: skin.secondaryText }}>
                {headline}
              </p>
            ) : null}
            <div
              className="mt-0.5 flex items-center gap-1 text-micro"
              style={{ color: skin.secondaryText }}
            >
              <span>{time}</span>
              <span aria-hidden="true">·</span>
              <GlobeIcon className="size-3" weight="fill" aria-label="Public" />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <DotsThreeIcon className="size-5" weight="bold" style={{ color: skin.secondaryText }} />
        </div>
      </header>

      {body ? (
        <div className="px-3 pb-3">
          <p
            className="whitespace-pre-wrap break-words text-sm leading-relaxed"
            style={{ color: skin.primaryText }}
          >
            {visible}
            {needsFold && !expanded ? (
              <>
                {'… '}
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  className="transition-opacity hover:underline hover:opacity-80"
                  style={{ color: skin.secondaryText }}
                >
                  see more
                </button>
              </>
            ) : null}
          </p>
        </div>
      ) : null}

      {documentUrl ? (
        <div className="border-y" style={{ borderColor: skin.cardBorder }} data-linkedin-document>
          <div
            className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-2 bg-muted"
            aria-hidden="true"
          >
            <FileTextIcon
              className="size-12"
              weight="regular"
              style={{ color: skin.secondaryText }}
            />
            <span className="text-detail font-semibold" style={{ color: skin.secondaryText }}>
              {(documentUrl.split(/[?#]/)[0].split('.').pop() ?? 'file').toUpperCase()}
            </span>
          </div>
          <div className="px-3 py-2.5" style={{ backgroundColor: skin.cardBackground }}>
            <p
              className="line-clamp-1 text-sm font-semibold leading-snug"
              style={{ color: skin.primaryText }}
            >
              {linkedInDocumentTitleFromUrl(documentUrl)}
            </p>
            <p className="mt-0.5 text-detail" style={{ color: skin.secondaryText }}>
              Document
            </p>
          </div>
        </div>
      ) : gridUrls ? (
        <div className="relative">
          <LinkedInImageGrid
            urls={gridUrls}
            activeIndex={safeActive}
            onSelect={onCarouselActiveIndexChange}
            focalX={focalX}
            focalY={focalY}
            alt={caption ?? `${brandName} post`}
            borderColor={skin.cardBackground}
          />
          {mediaOverlay}
        </div>
      ) : hasMedia ? (
        <MediaSlot
          ratio={resolvedAspect}
          bg="muted"
          imageUrl={currentImage}
          videoUrl={currentVideoUrl ?? videoUrl}
          videoPoster={currentVideoPoster ?? videoPoster}
          focalX={focalX}
          focalY={focalY}
          zoom={zoom}
          isLoading={isLoading}
          alt={caption ?? `${brandName} post`}
          overlay={
            mediaOverlay ? (
              <>
                {carouselOverlay}
                {mediaOverlay}
              </>
            ) : (
              carouselOverlay
            )
          }
        />
      ) : article ? (
        <div className="border-y" style={{ borderColor: skin.cardBorder }}>
          {article.imageUrl ? (
            <MediaSlot
              ratio={1.91}
              bg="muted"
              imageUrl={article.imageUrl}
              alt={article.title ?? article.domain}
            />
          ) : null}
          <div className="px-3 py-2.5" style={{ backgroundColor: skin.cardBackground }}>
            {article.title ? (
              <p
                className="line-clamp-2 text-sm font-semibold leading-snug"
                style={{ color: skin.primaryText }}
              >
                {article.title}
              </p>
            ) : null}
            <p className="mt-0.5 text-detail" style={{ color: skin.secondaryText }}>
              {article.domain}
            </p>
          </div>
        </div>
      ) : null}

      {likes != null || comments != null ? (
        <div
          className="flex items-center justify-between border-b px-3 py-2 text-detail"
          style={{ color: skin.secondaryText, borderColor: skin.cardBorder }}
        >
          {likes != null ? (
            <div className="flex items-center gap-1">
              <span className="size-4 rounded-full bg-platform-linkedin flex items-center justify-center">
                {/* Platform-native badge: the icon renders white on LinkedIn's
                    own blue regardless of Mora's theme — the badge fill isn't
                    a Mora surface, so bg-background/text-foreground don't
                    apply. Same treatment used throughout this directory for
                    platform chrome (see FacebookPost.tsx's identical badge). */}
                {/* eslint-disable-next-line no-restricted-syntax -- see comment above */}
                <ThumbsUpIcon className="size-2.5 text-white" weight="fill" />
              </span>
              <span>{compactNumber(likes)}</span>
            </div>
          ) : (
            <span />
          )}
          {comments != null ? <span>{compactNumber(comments)} comments</span> : null}
        </div>
      ) : null}

      <div className="grid grid-cols-4 px-1 py-0.5">
        <LiAction
          icon={<ThumbsUpIcon className="size-5" weight="regular" />}
          label="Like"
          textColor={skin.secondaryText}
        />
        <LiAction
          icon={<ChatCenteredIcon className="size-5" weight="regular" />}
          label="Comment"
          textColor={skin.secondaryText}
        />
        <LiAction
          icon={<RepeatIcon className="size-5" weight="regular" />}
          label="Repost"
          textColor={skin.secondaryText}
        />
        <LiAction
          icon={<PaperPlaneTiltIcon className="size-5" weight="regular" />}
          label="Send"
          textColor={skin.secondaryText}
        />
      </div>
    </article>
  );
};

const LiAction: React.FC<{ icon: React.ReactNode; label: string; textColor: string }> = ({
  icon,
  label,
  textColor,
}) => (
  <button
    type="button"
    className="flex items-center justify-center gap-1.5 rounded-md py-2 text-detail font-medium transition-colors"
    style={{ color: textColor }}
  >
    {icon}
    <span>{label}</span>
  </button>
);
